import React from 'react';
import { NavDropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { canAccessModule } from '../utils/permissionUtils';

const initials = (user) => {
  const first = (user?.firstName || '').trim();
  const last = (user?.lastName || '').trim();
  const letters = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  return letters || (user?.email || '?').charAt(0).toUpperCase();
};

/**
 * The signed-in person's menu (name, organization switch, Settings, Log out), the same in the app's header and on the
 * public pages, so someone who is signed in sees themselves wherever they are.
 */
function UserMenu() {
  const { user, logout, switchOrganization } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSwitchOrganization = async (organizationId) => {
    if (!organizationId || organizationId === user?.organizationId) {
      return;
    }
    const result = await switchOrganization(organizationId);
    if (result.success) {
      toast.success(`Switched to ${result.user.organizationName}`);
      navigate(result.user?.restricted ? '/org-admin' : '/dashboard');
      window.location.reload();
    } else {
      toast.error(typeof result.error === 'string' ? result.error : 'Failed to switch organization');
    }
  };

  const organizations = Array.isArray(user?.organizations) ? user.organizations : [];
  const hasMultipleOrgs = organizations.length > 1;

  return (
    <NavDropdown
      id="user-menu"
      align="end"
      className="user-menu"
      aria-label="Your account"
      title={(
        <span className="d-inline-flex align-items-center gap-2">
          <span className="user-avatar" aria-hidden="true">{initials(user)}</span>
          <span className="d-none d-lg-inline text-truncate user-menu-org">{user?.organizationName}</span>
        </span>
      )}
    >
      <NavDropdown.Header>
        <div className="fw-semibold text-body">{user?.firstName} {user?.lastName}</div>
        {user?.email && <div className="small">{user.email}</div>}
        {!hasMultipleOrgs && user?.organizationName && <div className="small">{user.organizationName}</div>}
      </NavDropdown.Header>
      {hasMultipleOrgs && (
        <>
          <NavDropdown.Divider />
          <NavDropdown.Header>Organization</NavDropdown.Header>
          {organizations.map((org) => (
            <NavDropdown.Item
              key={org.id}
              active={org.id === user?.organizationId}
              onClick={() => handleSwitchOrganization(org.id)}
            >
              {org.name}
            </NavDropdown.Item>
          ))}
        </>
      )}
      <NavDropdown.Divider />
      {canAccessModule(user, 'settings') && (
        <NavDropdown.Item onClick={() => navigate('/settings')}>
          <i className="bi bi-gear me-2" aria-hidden="true" />Settings
        </NavDropdown.Item>
      )}
      <NavDropdown.Item onClick={handleLogout}>
        <i className="bi bi-box-arrow-right me-2" aria-hidden="true" />Log out
      </NavDropdown.Item>
    </NavDropdown>
  );
}

export default UserMenu;
